import { signInWithPopup, signInWithRedirect } from "firebase/auth";
import isStandalone from "../../helpers/isStandalone";
import isProductionHost from "../../helpers/isProductionHost";
import logAnalyticsEvent from "./logAnalyticsEvent";

// A redirect sign-in that fails does NOT reject. When the SDK cannot read its
// pending state back — third-party storage partitioned away, the user
// abandoning the Google screen — getRedirectResult simply resolves null, which
// is indistinguishable from an ordinary first page load. This marker is what
// makes the difference visible: set on the way out, checked on the way back.
//
// sessionStorage survives the cross-origin round trip because it stays in the
// same tab, and is scoped to it, so a second tab cannot see a stale flag.
const REDIRECT_PENDING_KEY = "tgmq.redirectPending";

function markRedirectPending() {
  try {
    sessionStorage.setItem(REDIRECT_PENDING_KEY, "1");
  } catch {
    // Storage blocked. The redirect may still work; only the diagnostic is lost.
  }
}

// Reads and clears in one go, so the flag cannot outlive the attempt.
export function takeRedirectPending() {
  try {
    const pending = sessionStorage.getItem(REDIRECT_PENDING_KEY);
    sessionStorage.removeItem(REDIRECT_PENDING_KEY);
    return !!pending;
  } catch {
    return false;
  }
}

// Re-enable the button play.js disabled on click, so a failed sign-in can be
// retried. Without it the button keeps pointer-events: none until a reload.
function releaseSignInButton() {
  const signInBtn = document.getElementById("sign-in-with-google");
  if (signInBtn) signInBtn.disabled = false;
}

function logAuthError(error, GoogleAuthProvider) {
  console.log("########");
  console.log({ errorCode: error.code });
  console.log({ errorMessage: error.message });
  console.log({ email: error.customData?.email });
  console.log({ credential: GoogleAuthProvider.credentialFromError(error) });
  console.log("########");
}

// onFailure is optional and reports a sign-in that did not complete. Without it
// a rejection is invisible: releaseSignInButton puts the button back and the
// only other trace is a console.log that .babelrc removes in every env. It is
// called with (error, method), where method is "popup" or "redirect".
export default function firebaseAuthProviderWrapper(
  auth,
  GoogleAuthProvider,
  onFailure = () => {}
) {
  console.log("In firebaseAuthProviderWrapper...");

  const provider = new GoogleAuthProvider();

  // In an installed app, signInWithPopup hangs for ever.
  //
  // It works by opening the Firebase auth handler with window.open and waiting
  // for that window to postMessage the result back through window.opener. An
  // installed app has no opener relationship with the window it opens — the URL
  // is handed to a separate browser context — so the message never arrives, the
  // promise never settles, and the button just sits disabled with no error to
  // show. Redirect keeps the whole flow in this one context.
  //
  // Applied to every installed display mode, not just Android's: redirect is
  // equally correct wherever popup would have worked, so there is nothing to
  // gain from a narrower test.
  //
  // Success is picked up by the onAuthStateChanged observer on the way back in.
  // getRedirectResult in play.js only exists to surface a failed return.
  //
  // Production only, and the isProductionHost() half is load-bearing for local
  // development. The redirect flow needs the real Firebase auth handler served
  // same-origin through the /__/* proxy in netlify.toml. Neither exists on a
  // dev server, so against the Auth emulator the round trip always returns with
  // getRedirectResult resolving null — which play.js correctly reports as
  // "Sign-in was not completed", leaving local sign-in impossible for anyone
  // whose browser reports a standalone display mode. The popup path has no such
  // dependency and works against the emulator, so local dev always takes it.
  //
  // This changes nothing in production: isProductionHost() is true there, so
  // an installed app still gets the redirect it needs.
  if (isStandalone() && isProductionHost()) {
    // No sign_in_click here. logEvent waits on the analytics SDK's own init
    // promise and signInWithRedirect unloads the page on the same tick, so on a
    // cold start the event would never leave the device — while the
    // sign_in_success on the way back always sends. That is a funnel with a
    // numerator and no denominator, and a conversion rate above 100%.
    //
    // play.js logs it on the way back instead, off the marker set below, which
    // already survives this navigation. See the note there for what that
    // measures and what it cannot.
    markRedirectPending();
    signInWithRedirect(auth, provider).catch(error => {
      // Only reached if the navigation never starts; otherwise the page is gone.
      takeRedirectPending();
      releaseSignInButton();
      logAuthError(error, GoogleAuthProvider);
      // The click has to be logged here too. play.js replays it off the marker
      // above, but only on the way back, and this attempt never leaves. Without
      // it this failure has no click to divide into and the redirect failure
      // rate reads over 100%. The page is not unloading here, so it sends.
      logAnalyticsEvent("sign_in_click", { method: "redirect" });
      onFailure(error, "redirect");
    });
    return;
  }

  // The popup path never unloads the page, so this one is reliable, and it is
  // the path almost all traffic takes.
  logAnalyticsEvent("sign_in_click", { method: "popup" });

  signInWithPopup(auth, provider)
    .then(result => {
      console.log("Successful sign in...");
      // The popup half of sign_in_success. This resolves only after a real
      // attempt, unlike the auth observer, which also fires on every page load
      // of an already-signed-in user — put the event there and the ratio to
      // sign_in_click climbs past 100% and stops meaning anything.
      logAnalyticsEvent("sign_in_success", { method: "popup" });
      const user = result.user;
      console.log({ user });
    })
    .catch(error => {
      releaseSignInButton();
      logAuthError(error, GoogleAuthProvider);
      // Closing the popup yourself is a normal thing to do and is not worth a
      // banner; anything else is a failure the user cannot otherwise see.
      if (error?.code !== "auth/popup-closed-by-user")
        onFailure(error, "popup");
      // onFailure logs sign_in_failure, so only the abandoned case is left to
      // record here. It is worth its own event rather than the silence the
      // banner rule gives it: changing your mind and the sign-in being broken
      // look identical in the gap between sign_in_click and sign_in_success.
      //
      // Popup only. The redirect path reports the same act as a sign_in_failure
      // with code "no_result", because there it cannot be told apart from a
      // genuine breakage. See the comment on reportFailedSignIn in play.js.
      else logAnalyticsEvent("sign_in_abandoned", { method: "popup" });
    });
}
